'use strict'

import $ from 'jquery'
import _ from 'lodash'

import { $feedbackSpinnerFinnish, $feedbackSpinnerStart, DEBUG } from './helpers'
import { fetchCache, setCache } from './cache'
import { confirmPayment } from '../card/acquiredCardValidation'
import { mintReCaptchaTokenInto } from '../form/recaptcha'
import { ROUTE } from '../config'
import { selectors } from '../selectors'
import { $disableElement, $enableElement } from '../shared'

/**
 * resolve(null) -> skip
 * resolve(true) -> success
 * resolve(message) -> success with custom feedback
 * reject() -> failure
 * reject(message) -> failure with custom feedback
 */

let abortControllers = []

const abort = (selector) => {
    abortControllers[selector]?.abort()
    abortControllers[selector] = new AbortController()
}

const signal = (selector) => abortControllers[selector].signal

// ----------------------------------------------------
const extendedFetch = async (...args) => {
    const [url, options] = args
    const { selector, value } = options
    const $input = $(selector)

    abort(selector)
    $feedbackSpinnerStart($input) // TODO change BG ICON in Input

    // prettier-ignore
    const result = await Promise.any([
        fetchCache($input, value),
        fetch(url, { ...options, signal: signal(selector) }).then((response) => response.json()),
    ])
        .then((data) => setCache($input, value, data))

    $feedbackSpinnerFinnish($input)

    return result
}

// ----------------------------------------------------

export const emailExternal = ({ selector }, { resolve, reject }) => {
    const $input = $(selector)
    const email = _.trim($input.val())

    extendedFetch(ROUTE.validateEmail, {
        selector: selector,
        value: email,
        method: 'POST',
        body: JSON.stringify({ email }),
    })
        .then(({ isValid, message }) => {
            isValid ? resolve(true) : reject(message)
        })
        .catch((e) => console.warn(e) || (!(e instanceof AggregateError) && reject('Server error.')))
}

export const phoneExternal = ({ selector }, { resolve, reject }) => {
    const $input = $(selector)
    const phoneNumber = _.trim($input.val())

    extendedFetch(ROUTE.validatePhone, {
        selector: selector,
        value: phoneNumber,
        method: 'POST',
        body: JSON.stringify({ phoneNumber }),
    })
        .then(({ isValid, message }) => {
            isValid ? resolve(true) : reject(message)
        })
        .catch((e) => console.warn(e) || (!(e instanceof AggregateError) && reject('Server error.')))
}

export const personExternal = ({ selector }, { resolve, reject }) => {
    const $nameInput = $(selectors.inputs.name)
    const $surnameInput = $(selectors.inputs.surname)
    const $dobInput = $(selectors.inputs.dateOfBirth)

    // Some of the inputs is empty
    if (empty([$nameInput, $surnameInput, $dobInput])) {
        resolve(true)
        return
    }

    extendedFetch(ROUTE.validatePerson, {
        selector: selector,
        value: concat([$nameInput, $surnameInput, $dobInput]),
        method: 'POST',
        body: JSON.stringify({ name: _.trim($nameInput.val()), surname: _.trim($surnameInput.val()), dateOfBirth: _.trim($dobInput.val()) }),
    })
        .then(({ isValid, message }) => {
            isValid ? resolve(true) : reject(message)
        })
        .catch((e) => console.warn(e) || (!(e instanceof AggregateError) && reject('Server error.')))
}

// export const emailPinCodeExternal = ({ selector }, { resolve, reject }) => {
//     const $inputs = $(selector).parent().find('input')
//     const emailNumber = _.trim(
//         $inputs
//             .map((_, el) => $(el).val())
//             .get()
//             .join('')
//     )
//
//     console.log(emailNumber)
//     console.log(emailNumber.length)
//
//     // ??
//     if (emailNumber.length !== INPUT_LENGTH) {
//         return
//     }
//
//     extendedFetch(ROUTE.validatePinCode, {
//         selector: selector,
//         value: emailNumber,
//         method: 'POST',
//         body: JSON.stringify({ emailNumber }),
//     })
//         .then(({ isValid, message }) => {
//             isValid ? resolve(true) : reject(message)
//             // TODO isValid && $enableElement('nextButton') ???
//         })
//         .catch((e) => console.warn(e) || (!(e instanceof AggregateError) && reject('Server error.')))
// }

export const cardValidationExternal = ({ selector }, { resolve, reject }) => {
    const $input = $(selector)

    abort(selector)
    $disableElement($(selectors.submits.cardDetails))

    confirmPayment()
        .then(
            (response) => {
                DEBUG && console.log('acquiredResponse', response)

                if (response.data.status === 'error' && (response.data.component ?? false)) {
                    $enableElement($(selectors.submits.cardDetails))
                    reject('')
                    return
                }

                DEBUG && console.log('sending to backend')

                extendedFetch(ROUTE.validateCardDetails, {
                    selector: selector,
                    value: response.data,
                    method: 'POST',
                    body: JSON.stringify(response.data),
                })
                    .then(({ isValid, message }) => {
                        $input.attr('value', JSON.stringify(response.data))
                        if (isValid) {
                            $enableElement($(selectors.submits.cardDetails))
                            resolve(true)
                        } else {
                            $enableElement($(selectors.submits.cardDetails))
                            reject(message)
                        }
                    })
                    .catch((e) => console.warn(e) || (!(e instanceof AggregateError) && resolve(false)))
            },
            (error) => {
                DEBUG && console.log('server error', error)
                $enableElement($(selectors.submits.cardDetails))
                resolve(false)
            }
        )
        .catch((e) => {
            DEBUG && console.warn(e)
            $enableElement($(selectors.submits.cardDetails))
            resolve(false)
        })
}

// eslint-disable-next-line
export const address = ({ selector }, { resolve, reject }) => {
    // Done by SmartForm...

    resolve(true)
}

// ---------------------- Helpers ----------------------

const empty = ($inputs) => {
    return _.reduce($inputs, (result, $input) => result || $input._isEmpty(), false)
}

const concat = ($inputs) => {
    return _.reduce($inputs, (result, $input) => result + '|' + $input.val(), '')
}

// ----------------------------------------------------

/**
 * Mints the reCAPTCHA token into the form's hidden field, the same shape as the Acquired card rule:
 * a third-party SDK that has to run at submit time and leave a value behind for the server.
 *
 * Always resolves. A visitor who cannot reach Google submits an empty token and gets the server's
 * translated, retry-able message; rejecting here would put a validation error on a hidden input,
 * where it renders nowhere and simply kills the submit button.
 */
export const reCaptchaExternal = ({ selector }, { resolve }) => {
    mintReCaptchaTokenInto($(selector)).then(() => resolve(true))
}
